import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Home } from "lucide-react";
import { API, BRAND } from "@/lib/site";

export default function PaymentStatus() {
  const location = useLocation();
  const navigate = useNavigate();
  const isCancel = location.pathname.includes("cancel");
  const [state, setState] = useState(isCancel ? "cancel" : "polling");
  const [label, setLabel] = useState("");

  useEffect(() => {
    if (isCancel) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      if (attempts > 8) { setState("timeout"); return; }
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setLabel(data.label || "");
        if (data.payment_status === "paid") { setState("success"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [isCancel, location.search]);

  const config = {
    polling: { icon: <Loader2 size={56} className="text-primary animate-spin" />, title: "Confirming your payment…", desc: "Hang tight, this only takes a moment." },
    success: { icon: <CheckCircle2 size={56} className="text-secondary" />, title: "Payment successful!", desc: `Welcome aboard. ${label ? label + ", " : ""}our team will reach out to schedule your classes.` },
    cancel: { icon: <XCircle size={56} className="text-secondary" />, title: "Payment cancelled", desc: "No charge was made. You can pick a plan again whenever you're ready." },
    error: { icon: <XCircle size={56} className="text-destructive" />, title: "Something went wrong", desc: "We couldn't confirm your payment. If money was deducted, contact us and we'll sort it out." },
    timeout: { icon: <Loader2 size={56} className="text-primary" />, title: "Still processing", desc: "Your payment is taking longer than usual. We'll email you once it's confirmed." },
  }[state];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-5">
      <div className="bg-white rounded-3xl border border-black/5 shadow-xl p-10 md:p-14 max-w-md text-center" data-testid="payment-status">
        <div className="flex justify-center mb-6">{config.icon}</div>
        <h1 className="font-heading font-bold text-3xl">{config.title}</h1>
        <p className="text-muted-foreground mt-3">{config.desc}</p>
        <button data-testid="back-home-btn" onClick={() => navigate("/")} className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-white px-8 py-4 font-bold hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow] duration-300">
          <Home size={18} /> Back to {BRAND.name}
        </button>
      </div>
    </div>
  );
}
